<script setup>
import { computed } from 'vue'

/**
 * Alert / disclaimer card — Figma "Banners or Toasts" (26214:550354, 26214:550080).
 *
 *   • Variants: warning (yellow) · info (blue) · error (red) · success (green).
 *   • Leading icon sits BESIDE the copy, top-aligned, 10px gap.
 *   • Optional bold title and X close above the body.
 *   • Default slot is the body copy.
 *   • Named `#action` slot for trailing link/button (e.g. "Learn More").
 *
 * Two looks:
 *   • default — flat 50-level tint, gray-900 body. The Figma callout tints the
 *     box and the icon, not the copy. Used by the confirm dialogs.
 *   • `gradient` — the heavier yellow-300→400 card matching the CollX
 *     "Update CollX Settings" banner in
 *     `views/Connections/CollxDetails/ConnectionSettings.vue`.
 *
 * Flat is the default deliberately: it's what every current consumer wants, so
 * the heavy look can never show up by forgetting a prop.
 *
 * Usage:
 *   <AppAlert variant="warning" title="Heads up." dismissible @dismiss="…">
 *     Body copy here.
 *     <template #action>
 *       <a href="…" class="text-xs font-bold text-tint-1 hover:text-tint-2">Learn More</a>
 *     </template>
 *   </AppAlert>
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'warning',
    validator: (v) => ['warning', 'info', 'error', 'success'].includes(v),
  },
  icon:        { type: String,  default: '' },
  title:       { type: String,  default: '' },
  dismissible: { type: Boolean, default: false },
  // Edge-to-edge strip (no rounded corners) for use flush inside a panel/card —
  // e.g. a warning banner at the top of a dropdown. Default is a rounded card.
  flush:       { type: Boolean, default: false },
  /** Opt into the heavier gradient card (CollX banner look). */
  gradient:    { type: Boolean, default: false },

  /**
   * Copy + icon scale.
   *   • `sm` — 12px copy, 16px icon. The dense in-dialog/in-dropdown callout.
   *   • `md` — 14px copy, 24px icon. The section-level banner (Figma's Shopify
   *            location warning, 27156:281921), where the alert IS the block
   *            rather than a footnote inside one.
   */
  size:        { type: String, default: 'sm', validator: (v) => ['sm', 'md'].includes(v) },
})

defineEmits(['dismiss'])

// Each variant maps a (box, icon/title, body) triple. Only the box and the
// icon carry the hue — the copy stays gray-900 across all four, per Figma.
// Action slot consumers wire their own `text-tint-1` link to match the
// "Tint Color" token.
const palette = {
  warning: { box: 'bg-yellow-50', title: 'text-yellow-500', body: 'text-gray-900' },
  info:    { box: 'bg-blue-50',   title: 'text-tint-1',     body: 'text-gray-900' },
  error:   { box: 'bg-red-50',    title: 'text-red-600',    body: 'text-gray-900' },
  success: { box: 'bg-green-50',  title: 'text-green-600',  body: 'text-gray-900' },
}

// Heavier counterpart: the hue carries the whole card, so the body darkens to
// match rather than staying neutral.
const gradientPalette = {
  warning: {
    box:   'bg-gradient-to-b from-yellow-300 to-yellow-400',
    title: 'text-yellow-500',
    body:  'text-yellow-900',
  },
  info: {
    box:   'bg-gradient-to-b from-blue-100 to-blue-200',
    title: 'text-blue-800',
    body:  'text-blue-900',
  },
  error: {
    box:   'bg-gradient-to-b from-red-100 to-red-200',
    title: 'text-red-800',
    body:  'text-red-900',
  },
  success: {
    box:   'bg-gradient-to-b from-green-100 to-green-200',
    title: 'text-green-800',
    body:  'text-green-900',
  },
}

const tone = computed(() => (props.gradient ? gradientPalette : palette)[props.variant])

const isMedium = computed(() => props.size === 'md')
const iconSize = computed(() => (isMedium.value ? 'text-2xl' : 'text-base'))
const copySize = computed(() => (isMedium.value ? 'text-sm' : 'text-xs'))
</script>

<template>
  <!-- Icon sits BESIDE the copy, not above it: with an icon and no title the
       old header row put the glyph on its own line. -->
  <section role="alert" :class="[tone.box, flush ? 'rounded-none' : 'rounded-lg', gradient ? 'p-4' : 'p-3']">
    <div class="flex items-start" :class="gradient ? 'gap-x-2' : 'gap-x-[10px]'">
      <Icon
        v-if="icon"
        :icon="icon"
        class="mt-px shrink-0"
        :class="[iconSize, tone.title]"
        aria-hidden="true"
      />

      <div class="flex min-w-0 flex-1 flex-col justify-center gap-[2px]">
        <header
          v-if="title || $slots.title || dismissible"
          class="flex items-center gap-x-2"
        >
          <h3
            v-if="title || $slots.title"
            class="min-w-0 flex-1 font-semibold"
            :class="[copySize, tone.title]"
          >
            <slot name="title">{{ title }}</slot>
          </h3>

          <button
            v-if="dismissible"
            type="button"
            class="ml-auto flex-none transition hover:opacity-70"
            aria-label="Dismiss alert"
            @click="$emit('dismiss')"
          >
            <Icon icon="heroicons:x-mark" class="text-base" :class="tone.title" />
          </button>
        </header>

        <!-- Title-only alerts are a real shape (the Shopify location warning
             puts its body in a panel of its own beneath the strip); without the
             guard they render an empty paragraph and a stray gap. -->
        <p v-if="$slots.default" :class="[copySize, tone.body]">
          <slot />
        </p>

        <slot name="action" />
      </div>
    </div>
  </section>
</template>
